import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTablePreferences } from '../container';
import { layoutOrNone, useTableLayout } from './useTableLayout';

/**
 * How many rows a table shows at once. Section 10.3 of the request of
 * 16 September 2026.
 *
 * Not `All`. The server caps a page at two hundred, and an `All` that silently
 * stops at two hundred is a lie of exactly the kind this section exists to fix.
 */
export const PAGE_SIZES = [25, 50, 100, 200] as const;

/**
 * Twenty-five fills a laptop screen without scrolling the heading away, and is
 * the same on a phone: a card is taller than a row, and fifty of them is a long
 * scroll.
 */
export const DEFAULT_PAGE_SIZE = 25;

export interface TablePage {
  page: number;
  pageSize: number;
  /** Straight to a page, for Previous and Next. */
  goTo: (page: number) => void;
  /** A new size, remembered, and back to the first page. */
  choose: (size: number) => void;
  /**
   * Back to the first page. The screen calls this when a filter or the order
   * changes, because page three of the old list is nothing in the new one.
   */
  reset: () => void;
}

/**
 * The page a table is showing, and how big it is.
 *
 * The size is remembered per table, beside the sort preset and for the same
 * reasons; the page number is not, because which page somebody was on is a
 * position in a list that has changed since, not a preference.
 *
 * Every list endpoint has always returned `{ items, page, pageSize, total }`
 * and honoured both parameters. What the screens did was ask for the cap and
 * render the lot, so a lab with three hundred active cases lost a hundred of
 * them off the end with nothing on screen to say so. That is the defect behind
 * the request, and it is the `total` on screen, not the buttons, that fixes it.
 *
 * The size is kept on the person's ACCOUNT since the request of 7 October 2026,
 * section 3, in the table's layout beside its columns, so it is the same on
 * every device. This device still remembers the last size it showed, and uses
 * it for the moment before the account has answered, so the first request for
 * the list is already the right size on a device that has been here before.
 */
export function useTablePage(
  tableKey: string,
  fallbackSize = DEFAULT_PAGE_SIZE,
  sizes: readonly number[] = PAGE_SIZES,
): TablePage {
  const preferences = useTablePreferences();
  const account = useTableLayout(tableKey);

  const [page, setPage] = useState(1);
  const [deviceSize, setDeviceSize] = useState(() => {
    const remembered = preferences.pageSize(tableKey);
    // A size this table does not offer is somebody's old choice or a hand
    // edited value; the table's own default is the right answer to both.
    return remembered !== null && sizes.includes(remembered) ? remembered : fallbackSize;
  });

  // The account's, once it has answered: its size, or the table's own when it keeps none.
  const kept = account.layout?.pageSize ?? null;
  const pageSize = !account.loaded
    ? deviceSize
    : kept !== null && sizes.includes(kept)
      ? kept
      : fallbackSize;

  // So the next visit from this device starts at the size the account has.
  useEffect(() => {
    if (account.loaded && pageSize !== deviceSize) {
      setDeviceSize(pageSize);
      preferences.rememberPageSize(tableKey, pageSize);
    }
  }, [account.loaded, pageSize, deviceSize, preferences, tableKey]);

  const goTo = useCallback((next: number) => setPage(Math.max(1, next)), []);

  const choose = useCallback(
    (size: number) => {
      setDeviceSize(size);
      setPage(1);
      preferences.rememberPageSize(tableKey, size);
      // Beside the columns the account already keeps for this table, never instead of them.
      account.save({ ...layoutOrNone(account), pageSize: size });
    },
    [preferences, tableKey, account],
  );

  const reset = useCallback(() => setPage(1), []);

  return useMemo(
    () => ({ page, pageSize, goTo, choose, reset }),
    [page, pageSize, goTo, choose, reset],
  );
}
